import type { Vec2, Segment, AABB } from '../geo/types';
import type { SpatialHash } from '../geo/spatialHash';

export const NAV_EPSILON = 1e-4;

export interface SegmentHit {
  readonly point: Vec2;
  readonly distance: number;
  readonly normal: Vec2;
  readonly tangent: Vec2;
}

export interface SweepHit {
  readonly t: number;
  readonly center: Vec2;
  readonly normal: Vec2;
  readonly tangent: Vec2;
}

// The normal points from the segment toward p, so the walkable side of a boundary is either ±normal.
export function closestPointOnSegment(p: Vec2, seg: Segment): SegmentHit {
  const { a, b } = seg;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  const t =
    lenSq > NAV_EPSILON * NAV_EPSILON
      ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq))
      : 0;

  const point: Vec2 = [a[0] + t * dx, a[1] + t * dy];
  const len = Math.sqrt(lenSq);
  const tangent: Vec2 = len > NAV_EPSILON ? [dx / len, dy / len] : [1, 0];
  const rawNormal: Vec2 = [-tangent[1], tangent[0]];
  const side = (p[0] - point[0]) * rawNormal[0] + (p[1] - point[1]) * rawNormal[1];
  const normal: Vec2 = side >= 0 ? rawNormal : [-rawNormal[0], -rawNormal[1]];
  const distance = Math.hypot(p[0] - point[0], p[1] - point[1]);

  return { point, distance, normal, tangent };
}

// First contact of a circle moving by delta with a segment's line, within the segment plus radius.
function circleSegmentSweep(
  center: Vec2,
  delta: Vec2,
  radius: number,
  seg: Segment
): SweepHit | null {
  const { a, b } = seg;
  const segLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (segLen < NAV_EPSILON) return null;

  const tangent: Vec2 = [(b[0] - a[0]) / segLen, (b[1] - a[1]) / segLen];
  const normal: Vec2 = [-tangent[1], tangent[0]];
  const signedDist = (a[0] - center[0]) * normal[0] + (a[1] - center[1]) * normal[1];
  const velocity = delta[0] * normal[0] + delta[1] * normal[1];
  if (Math.abs(velocity) < NAV_EPSILON && Math.abs(signedDist) > radius) return null;

  const effectiveRadius = signedDist > 0 ? radius : -radius;
  const t = (signedDist - effectiveRadius) / velocity;
  if (!(t >= 0 && t <= 1)) return null;

  const hitCenter: Vec2 = [center[0] + delta[0] * t, center[1] + delta[1] * t];
  const along = (hitCenter[0] - a[0]) * tangent[0] + (hitCenter[1] - a[1]) * tangent[1];
  if (along < -radius || along > segLen + radius) return null;

  // The circle stops with its centre one radius off the line, on the side it came from.
  const awayFromWall: Vec2 = signedDist > 0 ? [-normal[0], -normal[1]] : normal;
  return { t, center: hitCenter, normal: awayFromWall, tangent };
}

export function sweepCircle(
  from: Vec2,
  delta: Vec2,
  radius: number,
  segments: SpatialHash<Segment>
): SweepHit | null {
  const to: Vec2 = [from[0] + delta[0], from[1] + delta[1]];
  const reach = radius + NAV_EPSILON;
  const box: AABB = {
    minX: Math.min(from[0], to[0]) - reach,
    minY: Math.min(from[1], to[1]) - reach,
    maxX: Math.max(from[0], to[0]) + reach,
    maxY: Math.max(from[1], to[1]) + reach,
  };

  let best: SweepHit | null = null;
  for (const seg of segments.query(box)) {
    const hit = circleSegmentSweep(from, delta, radius, seg);
    if (hit && hit.t > NAV_EPSILON && hit.t < 1 - NAV_EPSILON && (!best || hit.t < best.t)) {
      best = hit;
    }
  }
  return best;
}
